/**
 * @file SearchHaulageJobs.js
 * @version 1.8.6
 * @author Cam Seymour
 * @project Excel Agri System
 * @created 2026-07-29
 * @modified 2026-10-03
 * @description Search / manage Haulage Jobs (HO-). Trimmed copy of Search Customer
 *   Orders: no PO / bill linkage, supplier-centric, INV-H invoicing (single per job
 *   or bulk monthly per supplier). Resizable/sortable columns, three-dot + right-
 *   click row menu, matching Search Customer Orders.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_URL } from '../config';
import './SearchLayout.css';
import './SearchCustomerOrders.css';
import './ResizableColumns.css';
import usePersistedState from '../hooks/usePersistedState';
import useResizableColumns from '../hooks/useResizableColumns';
import useTableContextMenu from '../hooks/useTableContextMenu';
import useColumnFilters from '../hooks/useColumnFilters';
import DateRangeFilter from './DateRangeFilter';
import ColumnFilterMenu from './ColumnFilterMenu';
import ColumnFilterNote from './ColumnFilterNote';
import { activeHeaderFilters, clearFiltersClass } from '../utils/activeFilters';
import TableContextMenu from './TableContextMenu';
import RowActionsMenu from './RowActionsMenu';
import { useSlideOutButton } from './SlideOutButton';
import Input from './Input';
import Dropdown from './Dropdown';
import TickCheckbox from './TickCheckbox';
import PaginationControls from './PaginationControls';
import LoadingSpinner from './LoadingSpinner';
import ConfirmDialog from './ConfirmDialog';
import AlertDialog from './AlertDialog';
import ContactModal from './ContactModal';
import HaulageJobModal from './HaulageJobModal';
import { formatCurrency } from '../utils/currencyFormatter';
import { formatQuantity } from '../utils/numberFormatters';
import SearchHelp from './SearchHelp';
import useLatestRequest from '../hooks/useLatestRequest';
import { useReveal, revealFilters } from '../utils/revealInSearch';


// Also what Clear Filters resets to, and so what "no filters applied" means
// for the button's highlight and the toolbar pill (utils/activeFilters.js).
const DEFAULT_FILTERS = {
  search: '',
  date_from: '', date_to: '', date_type: 'delivery', datePreset: 'all-time',
  limit: '50', archived: false
};

/**
 * Columns filterable from their header. Keys must match the whitelist in
 * routes/haulageJobs.js; an unknown key narrows nothing.
 */
const COLUMN_FILTERS = {
  supplier_name: { label: 'Supplier' },
  customer_name: { label: 'Delivery customer' },
  product_details: { label: 'Product' },
  units: { label: 'Units' },
  /* Delivery and Invoiced were two dropdowns in the filter row. They are two
     independent facts about a job — the icons in this cell already show both —
     so they are sections under the header those icons sit under. */
  status: {
    label: 'Status',
    sections: [
      { key: 'delivery_status', label: 'Delivery' },
      { key: 'invoiced_status', label: 'Invoiced' },
      { key: 'moffett', label: 'Moffett' }
    ]
  }
};

function SearchHaulageJobs({ user, onNavigate }) {
  // Arriving from a modal's "Find in …" (utils/revealInSearch.js): the term
  // seeds the filters and the page goes back to 1 in the FIRST render's state.
  const reveal = useReveal('haulage-jobs');
  const [filters, setFilters] = usePersistedState('searchHaulageJobs.filters', DEFAULT_FILTERS,
    { seed: revealFilters(reveal) });
  const [page, setPage] = usePersistedState('searchHaulageJobs.page', 1, { seed: reveal ? 1 : null });
  const [sortField, setSortField] = usePersistedState('searchHaulageJobs.sortField', 'created_at');
  const [sortDirection, setSortDirection] = usePersistedState('searchHaulageJobs.sortDirection', 'desc');
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 1 });
  const [jobs, setJobs] = useState([]);
  // Distinct values per filterable column across the whole result set.
  const [facets, setFacets] = useState({});
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);
  const [contactModalId, setContactModalId] = usePersistedState('searchHaulageJobs.contactModalId', null);

  /*
   * Resizable columns (13) + right-click context menu, mirroring Search Orders.
   *
   * The page key carries a version because widths persist by column INDEX, and
   * Units, Rate and Moffett were inserted mid-table — a width saved against
   * the old ten-column layout would land on the wrong column.
   */
  const { tableRef, ResizeHandle, ColGroup, ResetButton } = useResizableColumns('searchHaulageJobs.v2', 13);
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);
  // Right-click a header to narrow by the values on this page.
  const cols = useColumnFilters(jobs, COLUMN_FILTERS, {
    facets,
    storageKey: 'haulageJobs.columnFilters',
    onChange: () => setFilters(prev => ({ ...prev, page: 1 }))
  });

  const ctxMenu = useTableContextMenu({ rows: jobs, selectedIds: selectedIdSet, getRowId: (j) => j.id });

  const [viewJobId, setViewJobId] = usePersistedState('searchHaulageJobs.viewJobId', null);
  const [bulkInvoicing, setBulkInvoicing] = useState(false);

  const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, message: '', onConfirm: null });
  const [alertDialog, setAlertDialog] = useState({ isOpen: false, message: '', type: 'error' });

  useSlideOutButton([selectedIds.length, loading, bulkInvoicing]);

  // Typing fires a search per pause and the responses can land out of order —
  // only the newest may write the table.
  const latest = useLatestRequest();

  const fetchJobs = useCallback(async () => {
    const seq = latest.begin();
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.search) params.append('search', filters.search);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);
      if (filters.date_type) params.append('date_type', filters.date_type);
      if (filters.archived) params.append('archived', 'true');
      if (cols.queryValue) params.append('columnFilters', cols.queryValue);
      params.append('sort_field', sortField);
      params.append('sort_direction', sortDirection);
      params.append('limit', filters.limit || '50');
      params.append('page', String(page));
      const response = await fetch(`${API_URL}/api/haulage-jobs/search?${params.toString()}`, { credentials: 'include' });
      const data = await response.json();
      if (!latest.isLatest(seq)) return;   // superseded by a later search
      setJobs(data.jobs || []);
      setFacets(data.facets || {});
      if (data.pagination) setPagination(data.pagination);
    } catch (error) {
      if (!latest.isLatest(seq)) return;
      console.error('Failed to fetch haulage jobs:', error);
    } finally {
      if (latest.isLatest(seq)) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page, sortField, sortDirection, cols.queryValue]);

  useEffect(() => {
    const t = setTimeout(fetchJobs, 250);
    return () => clearTimeout(t);
  }, [fetchJobs]);

  // Changing any filter resets to the first page; only changePage moves pages.
  const setFilter = (key, value) => { setFilters(prev => ({ ...prev, [key]: value })); setPage(1); };
  // Column filters are filters too; the button says it clears all of them.
  const clearFilters = () => { setFilters(DEFAULT_FILTERS); cols.clearEverything(); setPage(1); };

  // Any filter at all — header or column — lights the Clear Filters button.
  // The date keys are renamed to the ones activeHeaderFilters treats as one
  // Date filter; date_type only says which date the range applies to.
  const asDateKeys = (f) => ({ ...f, dateFrom: f.date_from, dateTo: f.date_to });
  const headerFilters = activeHeaderFilters(asDateKeys(filters), asDateKeys(DEFAULT_FILTERS), {
    ignore: ['date_from', 'date_to', 'date_type']
  });
  const filtersApplied = headerFilters.count > 0 || cols.activeCount > 0;
  const changePage = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) setPage(newPage);
  };

  const handleSort = (field) => {
    if (field === sortField) setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortDirection('asc'); }
    setPage(1);
  };
  const sortArrow = (field) => sortField === field ? (sortDirection === 'asc' ? ' ▲' : ' ▼') : '';

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const deliveryTo = (job) => job.customer_name || (job.manual_entry && job.manual_entry.name) || '—';

  const toggleSelect = (id) => setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const selectableJobs = jobs.filter(j => j.invoiced_status !== 'Invoiced');
  const allSelected = selectableJobs.length > 0 && selectableJobs.every(j => selectedIds.includes(j.id));
  const toggleSelectAll = () => setSelectedIds(allSelected ? [] : selectableJobs.map(j => j.id));

  // ─── Actions ───────────────────────────────────────────────────────────────
  const handleGenerateInvoice = (job) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Generate Invoice',
      message: `Generate an INV-H invoice to ${job.supplier_name} for haulage job ${job.job_number}?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const response = await fetch(`${API_URL}/api/haulage-jobs/${job.id}/generate-invoice`, {
            method: 'POST', credentials: 'include'
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || 'Request failed');
          setAlertDialog({ isOpen: true, message: `Created invoice ${data.invoice_number}.${data.xeroMessage ? ` ${data.xeroMessage}` : ''}`, type: 'success' });
          fetchJobs();
        } catch (error) {
          setAlertDialog({ isOpen: true, message: `Failed to generate invoice: ${error.message}`, type: 'error' });
        }
      }
    });
  };

  const handleBulkInvoice = () => {
    if (!selectedIds.length) return;
    setConfirmDialog({
      isOpen: true,
      title: 'Generate Monthly Invoices',
      message: `Generate INV-H invoices for ${selectedIds.length} job(s), grouped into one invoice per supplier?`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        setBulkInvoicing(true);
        try {
          const response = await fetch(`${API_URL}/api/haulage-jobs/bulk/generate-invoice`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ job_ids: selectedIds })
          });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || 'Request failed');
          setAlertDialog({ isOpen: true, message: `Created ${data.invoices?.length || 0} invoice(s): ${(data.invoices || []).map(i => i.invoice_number).join(', ')}.${data.xeroMessage ? ` ${data.xeroMessage}` : ''}`, type: 'success' });
          setSelectedIds([]);
          fetchJobs();
        } catch (error) {
          setAlertDialog({ isOpen: true, message: `Failed to generate invoices: ${error.message}`, type: 'error' });
        } finally {
          setBulkInvoicing(false);
        }
      }
    });
  };

  const handleDelete = (job) => {
    setConfirmDialog({
      isOpen: true,
      title: 'Delete Haulage Job',
      message: `Delete haulage job ${job.job_number}? This cannot be undone.`,
      onConfirm: async () => {
        setConfirmDialog({ isOpen: false, message: '', onConfirm: null });
        try {
          const response = await fetch(`${API_URL}/api/haulage-jobs/${job.id}`, { method: 'DELETE', credentials: 'include' });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || 'Request failed');
          fetchJobs();
        } catch (error) {
          setAlertDialog({ isOpen: true, message: `Failed to delete: ${error.message}`, type: 'error' });
        }
      }
    });
  };

  const handleArchive = async (job) => {
    const archived = !job.is_archived;
    try {
      const response = await fetch(`${API_URL}/api/haulage-jobs/${job.id}/archive`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ archived })
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data.error || 'Request failed');
      fetchJobs();
    } catch (error) {
      setAlertDialog({ isOpen: true, message: `Failed to ${archived ? 'archive' : 'restore'}: ${error.message}`, type: 'error' });
    }
  };

  /*
   * Editing opens the Create Haulage Job screen, the way every other entity in
   * the system is edited — Create Bill, Create Purchase Order, Create Invoice
   * and Create Customer Order all load an existing record into their own form.
   *
   * This screen used to carry a cut-down modal of its own that offered seven
   * of a job's fields and quietly lacked the rate picker and the delivery
   * customer, so a rate could be set when a job was raised and never corrected.
   * One form, and the two cannot drift.
   */
  const openEdit = (job) => {
    if (!onNavigate) return;
    sessionStorage.setItem('editHaulageJobId', job.id);
    onNavigate('create-haulage-job');
  };

  const statusIcon = (icon, active, color, title) => (
    <span className="material-symbols-rounded status-icon" title={title}
      style={{ color: active ? `var(--${color})` : 'var(--text-muted)', opacity: active ? 1 : 0.35 }}>
      {icon}
    </span>
  );

  // Shared by the row's three-dot menu and the right-click context menu, so the
  // two surfaces can never drift apart.
  const buildRowActions = (job) => {
    const invoiced = job.invoiced_status === 'Invoiced';
    return [
      {
        key: 'view', readSafe: true, icon: 'visibility', label: 'View',
        title: `Open ${job.job_number}`,
        onClick: () => setViewJobId(job.id)
      },
      {
        key: 'invoice', icon: 'receipt_long', label: 'Generate Invoice',
        disabled: invoiced, title: invoiced ? 'Already invoiced' : 'Generate INV-H invoice',
        onClick: () => handleGenerateInvoice(job)
      },
      {
        key: 'edit', icon: 'edit', label: 'Edit',
        disabled: invoiced, title: invoiced ? 'Cannot edit an invoiced job' : 'Edit',
        onClick: () => openEdit(job)
      },
      {
        key: 'archive', icon: job.is_archived ? 'unarchive' : 'archive',
        label: job.is_archived ? 'Restore' : 'Archive',
        onClick: () => handleArchive(job)
      },
      {
        key: 'delete', icon: 'delete', label: 'Delete', className: 'action-danger',
        disabled: invoiced, title: invoiced ? 'Cannot delete an invoiced job' : 'Delete',
        onClick: () => handleDelete(job)
      }
    ];
  };

  const bulkActions = (ids) => [
    {
      key: 'bulk-invoice', icon: 'receipt_long',
      label: `Generate invoices for ${ids.size}`,
      onClick: handleBulkInvoice
    }
  ];

  return (
    <div className="search-customer-orders haulage-jobs">
      <div className="search-header">
        <div className="workflow-info" style={{ gridColumn: '1' }}>
          <span className="info">Search and manage haulage jobs</span>
          <span className="note">
            <span className="material-symbols-rounded">info</span>
            <span style={{ margin: 'auto 0' }}>Invoice suppliers for haulage — group a month of jobs per supplier into one INV-H invoice</span>
          </span>
        </div>
        <div className="search-header-inner" style={{ gridColumn: '2' }}>
          <div className="buttons slideout">
            <div>
              <button
                className={clearFiltersClass(filtersApplied)}
                onClick={clearFilters}
                title={filtersApplied ? 'Clear all filters' : 'No filters applied'}
              >
                <div className="icon-wrapper"><span className="material-symbols-rounded">filter_alt_off</span></div>
                <span className="btn-label">Clear Filters</span>
              </button>
            </div>
            <div>
              <button className="btn-slideout" onClick={() => onNavigate && onNavigate('create-haulage-job')}>
                <div className="icon-wrapper"><span className="material-symbols-rounded">add</span></div>
                <span className="btn-label">Create Haulage Job</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="search-filters">
        <div className="filter-row">
          <div className="filter-group">
            <label>
              Search
              <SearchHelp
                fields={['job no.', 'supplier', 'customer', 'product']}
              />
            </label>
            <Input type="text" placeholder="HO number, supplier, customer, or product"
              value={filters.search} onChange={e => setFilter('search', e.target.value)}
              onEnter={() => fetchJobs()} icon="search" />
          </div>
          {/* Delivery and Invoiced have gone from the row: both are sections
              under the Status header, where the icons that show them already
              are, and there they narrow the whole result set. */}
          <DateRangeFilter
            from={filters.date_from}
            to={filters.date_to}
            preset={filters.datePreset}
            onChange={({ from, to, preset }) => setFilters(prev => ({
              ...prev, date_from: from, date_to: to, datePreset: preset
            }))}
          />
          <div className="filter-group">
            <label>Date Type</label>
            <Dropdown value={filters.date_type} onChange={v => setFilter('date_type', v)}
              options={[
                { value: 'delivery', label: 'Delivery' },
                { value: 'ordered', label: 'Ordered' }
              ]} placeholder="Select..." />
          </div>
          <div className="filter-group">
            <label>Per Page</label>
            <Dropdown value={filters.limit} onChange={v => setFilter('limit', v)}
              options={[
                { value: '20', label: '20' },
                { value: '50', label: '50' },
                { value: '100', label: '100' },
                { value: '200', label: '200' }
              ]} placeholder="50" />
          </div>
          <div className="filter-group">
            <label>Archived</label>
            <div className="checkbox-container">
              <div className="check light">
                <input id="ho-archived" type="checkbox" checked={filters.archived}
                  onChange={e => setFilter('archived', e.target.checked)} />
                <label htmlFor="ho-archived"></label>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="orders-panel">
        {/* Bulk actions bar — sits at the top of the panel, same as Search Orders */}
        {selectedIds.length > 0 && (
          <div className="bulk-actions-bar">
            <div className="bulk-actions-info">
              <span className="bulk-actions-title">Bulk Actions:</span>
              <span>{selectedIds.length} item{selectedIds.length !== 1 ? 's' : ''} selected</span>
            </div>
            <div className="bulk-actions-buttons slideout">
              <div>
                <button className="btn-slideout" onClick={handleBulkInvoice} disabled={bulkInvoicing}>
                  <div className="icon-wrapper">
                    <span className={`material-symbols-rounded${bulkInvoicing ? ' spinning' : ''}`}>{bulkInvoicing ? 'progress_activity' : 'receipt_long'}</span>
                  </div>
                  <span className="btn-label">{bulkInvoicing ? 'Generating…' : 'Generate Monthly Invoices'}</span>
                </button>
              </div>
              <div>
                <button data-read-safe className="btn-slideout" onClick={() => setSelectedIds([])}>
                  <div className="icon-wrapper"><span className="material-symbols-rounded">close</span></div>
                  <span className="btn-label">Clear</span>
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Toolbar: reset-columns (left) + pagination (right), always visible */}
        <div className="table-toolbar">
          <div style={{ display: 'flex', gap: 'var(--spacing-sm)', alignItems: 'center' }}>
            <ResetButton />
            <ColumnFilterNote cols={cols} header={headerFilters} onClear={clearFilters} />
          </div>
          <PaginationControls pagination={pagination} onPageChange={changePage} />
        </div>

        <div className="table-container">
        <table className="data-table resizable-table table-min-lg" ref={tableRef} onContextMenu={ctxMenu.onTableContextMenu}>
          <ColGroup />
          <thead>
            <tr>
              <th className="checkbox-col no-sort">
                <TickCheckbox id="ho-select-all" checked={allSelected} onChange={toggleSelectAll} ariaLabel="Select all" disabled={selectableJobs.length === 0} />
                <ResizeHandle colIndex={0} />
              </th>
              <th className="sortable" onClick={() => handleSort('job_number')}>Job #{sortArrow('job_number')}<ResizeHandle colIndex={1} /></th>
              <th className={`sortable ${cols.headerProps('supplier_name').className || ''}`.trim()}
                  onClick={() => handleSort('supplier_name')}
                  onContextMenu={cols.headerProps('supplier_name').onContextMenu}
                  title="Right-click to filter by supplier">Supplier{sortArrow('supplier_name')}<ResizeHandle colIndex={2} /></th>
              <th className={`sortable ${cols.headerProps('customer_name').className || ''}`.trim()}
                  onClick={() => handleSort('customer_name')}
                  onContextMenu={cols.headerProps('customer_name').onContextMenu}
                  title="Right-click to filter by delivery customer">Delivery Customer{sortArrow('customer_name')}<ResizeHandle colIndex={3} /></th>
              <th className={`sortable ${cols.headerProps('product_details').className || ''}`.trim()}
                  onClick={() => handleSort('product_details')}
                  onContextMenu={cols.headerProps('product_details').onContextMenu}
                  title="Right-click to filter by product">Product{sortArrow('product_details')}<ResizeHandle colIndex={4} /></th>
              <th className="sortable text-right" onClick={() => handleSort('quantity')}>Qty{sortArrow('quantity')}<ResizeHandle colIndex={5} /></th>
              <th className={`sortable ${cols.headerProps('units').className || ''}`.trim()}
                  onClick={() => handleSort('units')}
                  onContextMenu={cols.headerProps('units').onContextMenu}
                  title="Right-click to filter by units">Units{sortArrow('units')}<ResizeHandle colIndex={6} /></th>
              <th className="sortable" onClick={() => handleSort('created_at')}>Created{sortArrow('created_at')}<ResizeHandle colIndex={7} /></th>
              <th className="sortable" onClick={() => handleSort('delivery_date')}>Delivery{sortArrow('delivery_date')}<ResizeHandle colIndex={8} /></th>
              <th className="sortable text-right" onClick={() => handleSort('rate')}>Rate{sortArrow('rate')}<ResizeHandle colIndex={9} /></th>
              <th className="sortable text-right" onClick={() => handleSort('moffett_charge')}>Moffett{sortArrow('moffett_charge')}<ResizeHandle colIndex={10} /></th>
              <th className={`no-sort ${cols.headerProps('status').className || ''}`.trim()}
                  style={{ textAlign: 'center' }}
                  onContextMenu={cols.headerProps('status').onContextMenu}
                  title="Right-click to filter by delivery, invoiced or Moffett">Status<ResizeHandle colIndex={11} /></th>
              <th className="actions-col no-sort"><ResizeHandle colIndex={12} /></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan="13"><LoadingSpinner /></td></tr>
            ) : cols.rows.length === 0 ? (
              <tr><td colSpan="13" className="no-results">No haulage jobs found</td></tr>
            ) : cols.rows.map(job => {
              const invoiced = job.invoiced_status === 'Invoiced';
              const isSelected = selectedIds.includes(job.id);
              return (
                <tr key={job.id}
                    className={`clickable-row ${isSelected ? 'selected' : ''} ${job.is_archived ? 'row-archived' : ''}`.trim()}
                    onClick={() => setViewJobId(job.id)}
                    onContextMenu={ctxMenu.onRowContextMenu(job)}>
                  <td className="checkbox-col" onClick={(e) => e.stopPropagation()}>
                    <TickCheckbox id={`ho-${job.id}`} checked={isSelected}
                      onChange={() => toggleSelect(job.id)} ariaLabel={`Select ${job.job_number}`} disabled={invoiced} />
                  </td>
                  <td><span className="order-number-display">{job.job_number}</span></td>
                  {/* Names only, both clickable through to the contact — the
                      same shape the customer and supplier columns carry on
                      Search Customer Orders. */}
                  <td>
                    {job.supplier_account_id ? (
                      <span className="contact-link supplier-name"
                            onClick={(e) => { e.stopPropagation(); setContactModalId(job.supplier_account_id); }}>
                        {job.supplier_name}
                      </span>
                    ) : <span>{job.supplier_name || '—'}</span>}
                  </td>
                  <td>
                    {job.customer_account_id ? (
                      <span className="contact-link supplier-name"
                            onClick={(e) => { e.stopPropagation(); setContactModalId(job.customer_account_id); }}>
                        {deliveryTo(job)}
                      </span>
                    ) : <span>{deliveryTo(job)}</span>}
                  </td>
                  <td>{job.product_details || '—'}</td>
                  <td className="text-right">{job.quantity != null ? formatQuantity(job.quantity) : '—'}</td>
                  <td>{job.units || '—'}</td>
                  <td>{formatDate(job.created_at)}</td>
                  <td>{formatDate(job.delivery_date)}</td>
                  <td className="text-right">{job.rate != null ? formatCurrency(job.rate) : '—'}</td>
                  {/* Blank, not £0.00, when there is no Moffett: the charge is
                      optional and a zero reads as one that was charged. */}
                  <td className="text-right">
                    {Number(job.moffett_charge) > 0 ? formatCurrency(job.moffett_charge) : '—'}
                  </td>
                  <td className="status-col">
                    <div className="status-icons">
                      {job.is_archived && <span className="archived-badge">Archived</span>}
                      {statusIcon('local_shipping', job.delivery_status === 'Delivered', 'success', `Delivery: ${job.delivery_status}`)}
                      {statusIcon('receipt_long', invoiced, 'success', invoiced ? `Invoiced (${job.invoice_number || ''})` : 'Not invoiced')}
                    </div>
                  </td>
                  <td className="actions-col" onClick={(e) => e.stopPropagation()}>
                    <div className="actions-cell">
                      <RowActionsMenu actions={buildRowActions(job)} />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <TableContextMenu ctx={ctxMenu} rowActions={buildRowActions} bulkActions={bulkActions} />

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() => setConfirmDialog({ isOpen: false, message: '', onConfirm: null })}
      />
      <AlertDialog
        isOpen={alertDialog.isOpen}
        message={alertDialog.message}
        type={alertDialog.type}
        onClose={() => setAlertDialog({ ...alertDialog, isOpen: false })}
      />
      <ColumnFilterMenu cols={cols} />

      {/* Read the job; Edit from its footer opens the Create screen loaded
          with it. Before this the only way in was a cut-down edit form. */}
      <HaulageJobModal
        jobId={viewJobId}
        onClose={() => { setViewJobId(null); fetchJobs(); }}
        onEdit={(job) => { setViewJobId(null); openEdit(job); }}
      />

      {contactModalId && (
        <ContactModal contactId={contactModalId} onClose={() => setContactModalId(null)} />
      )}
    </div>
  );
}

export default SearchHaulageJobs;
